import { commonValidationLabels } from "@/i18n/labels/commonLabels";
import { getLanguage, type Language } from "@/utils/languageUtils";

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface FormValidationResult {
  isValid: boolean;
  errors: Record<string, string[]>;
}

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  requirements: {
    minLength: boolean;
    hasLowercase: boolean;
    hasUppercase: boolean;
    hasNumber: boolean;
    hasSpecialChar: boolean;
  };
}

export interface NameValidationMessages {
  nameRequired?: string;
  nameNoDigits?: string;
  nameLettersOnly?: string;
  nameFirstAndLastName?: string;
  nameMinLength?: string;
}


export const validateName = (name: string, messages?: NameValidationMessages, lang?: Language): ValidationResult => {
  const v = commonValidationLabels(lang ?? getLanguage());
  const m = { ...{ nameRequired: v.nameRequired, nameNoDigits: v.nameNoDigits, nameLettersOnly: v.nameLettersOnly, nameFirstAndLastName: v.nameFirstAndLastName, nameMinLength: v.nameMinLength }, ...messages };
  const errors: string[] = [];

  if (!name.trim()) {
    errors.push(m.nameRequired!);
  } else {
    if (/\d/.test(name)) errors.push(m.nameNoDigits!);
    if (!/^[A-ZÅÄÖa-zåäö\s]+$/.test(name)) errors.push(m.nameLettersOnly!);
    if (name.trim().split(/\s+/).length < 2) errors.push(m.nameFirstAndLastName!);
    if (name.length < 3) errors.push(m.nameMinLength!);
  }

  return { isValid: errors.length === 0, errors };
};

export interface PersonalNumberValidationMessages {
  personalNumberRequired?: string;
  personalNumberFormat?: string;
}


export const validatePersonalNumber = (personalNumber: string, messages?: PersonalNumberValidationMessages, lang?: Language): ValidationResult => {
  const v = commonValidationLabels(lang ?? getLanguage());
  const m = { ...{ personalNumberRequired: v.personalNumberRequired, personalNumberFormat: v.personalNumberFormat }, ...messages };
  const errors: string[] = [];

  if (!personalNumber.trim()) {
    errors.push(m.personalNumberRequired!);
  } else {
    const cleaned = personalNumber.replace(/-/g, '');
    if (!/^\d{12}$/.test(cleaned) && !/^\d{8}\d{4}$/.test(cleaned)) {
      errors.push(m.personalNumberFormat!);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Helper function to format personal number with dash after 8 digits
export const formatPersonalNumber = (value: string): string => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // If we have at least 8 digits, insert dash after 8th digit
  if (digits.length >= 8) {
    return digits.slice(0, 8) + '-' + digits.slice(8, 12);
  }

  return digits;
};

/** YYMMDD-XXXX format (10 digits), hyphen before last 4. Used in dealer portal. */
export const formatPersonalNumberYYMMDD = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length >= 6) {
    return digits.slice(0, 6) + '-' + digits.slice(6, 10);
  }
  return digits;
};

/** Normalize existing personnummer for display: 12 digits -> use YYMMDD+4 (drop century), else first 10. */
export const normalizePersonalNumberYYMMDD = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  const use = digits.length >= 12 ? digits.slice(2, 12) : digits.slice(0, 10);
  return formatPersonalNumberYYMMDD(use);
};

/** Validate optional personnummer in YYMMDD-XXXX format (10 digits). */
export const validatePersonalNumberYYMMDD = (
  value: string,
  messages?: { personalNumberFormat?: string },
  lang?: Language
): ValidationResult => {
  const v = commonValidationLabels(lang ?? getLanguage());
  const msg = messages?.personalNumberFormat ?? (v as Record<string, string>).personalNumberFormatYYMMDD ?? v.personalNumberFormat;
  const m = { personalNumberFormat: msg, ...messages };
  const errors: string[] = [];
  const trimmed = value.trim();
  if (!trimmed) return { isValid: true, errors: [] };
  const cleaned = trimmed.replace(/-/g, '');
  if (!/^\d{10}$/.test(cleaned)) {
    errors.push(m.personalNumberFormat!);
  }
  return { isValid: errors.length === 0, errors };
};

export interface EmailValidationMessages {
  emailRequired?: string;
  invalidEmail?: string;
}

const DEFAULT_EMAIL_MESSAGES: EmailValidationMessages = {
  emailRequired: "E-postadress är obligatorisk",
  invalidEmail: "Ogiltig e-postadress",
};

export const validateEmail = (email: string, messages?: EmailValidationMessages, lang?: Language): ValidationResult => {
  const v = commonValidationLabels(lang ?? getLanguage());
  const m = { ...{ emailRequired: v.emailRequired, invalidEmail: v.invalidEmail }, ...messages };
  const errors: string[] = [];

  if (!email.trim()) {
    errors.push(m.emailRequired!);
  } else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) errors.push(m.invalidEmail!);
  }

  return { isValid: errors.length === 0, errors };
};

export interface PhoneValidationMessages {
  phoneRequired?: string;
  phoneFormat?: string;
  phoneMinLength?: string;
}

const DEFAULT_PHONE_MESSAGES: PhoneValidationMessages = {
  phoneRequired: "Telefonnummer är obligatoriskt",
  phoneFormat: "Telefonnummer får endast innehålla siffror, +, mellanslag, - och ()",
  phoneMinLength: "Telefonnummer måste vara minst 7 siffror",
};

export const validatePhone = (phone: string, messages?: PhoneValidationMessages): ValidationResult => {
  const m = { ...DEFAULT_PHONE_MESSAGES, ...messages };
  const errors: string[] = [];

  if (!phone.trim()) {
    errors.push(m.phoneRequired!);
  } else {
    const cleanPhone = phone.replace(/[\s\-()]/g, '');
    if (!/^[\d+]+$/.test(cleanPhone)) errors.push(m.phoneFormat!);
    if (cleanPhone.replace(/\D/g, '').length < 7) errors.push(m.phoneMinLength!);
  }

  return { isValid: errors.length === 0, errors };
};

export interface CompanyNameValidationMessages {
  companyNameRequired?: string;
  companyNameMinLength?: string;
}

const DEFAULT_COMPANY_NAME_MESSAGES: CompanyNameValidationMessages = {
  companyNameRequired: "Företagsnamn är obligatoriskt",
  companyNameMinLength: "Företagsnamn måste vara minst 2 tecken långt",
};

export const validateCompanyName = (companyName: string, messages?: CompanyNameValidationMessages, lang?: Language): ValidationResult => {
  const v = commonValidationLabels(lang ?? getLanguage());
  const m = { ...{ companyNameRequired: v.companyNameRequired, companyNameMinLength: v.companyNameMinLength }, ...messages };
  const errors: string[] = [];

  if (!companyName.trim()) {
    errors.push(m.companyNameRequired!);
  } else {
    if (companyName.length < 2) {
      errors.push(m.companyNameMinLength!);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export interface OrgNumberValidationMessages {
  orgNumberRequired?: string;
  orgNumberFormat?: string;
}

const DEFAULT_ORG_NUMBER_MESSAGES: OrgNumberValidationMessages = {
  orgNumberRequired: "Organisationsnummer är obligatoriskt",
  orgNumberFormat: "Organisationsnummer måste vara 10 siffror i formatet 556123-4567",
};

export const validateOrgNumber = (orgNumber: string, messages?: OrgNumberValidationMessages, lang?: Language): ValidationResult => {
  const v = commonValidationLabels(lang ?? getLanguage());
  const m = { ...{ orgNumberRequired: v.orgNumberRequired, orgNumberFormat: v.orgNumberFormat }, ...messages };
  const errors: string[] = [];

  if (!orgNumber.trim()) {
    errors.push(m.orgNumberRequired!);
  } else {
    const cleanNumber = orgNumber.replace(/[\s-]/g, '');
    if (!/^\d{10}$/.test(cleanNumber)) {
      errors.push(m.orgNumberFormat!);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export interface AddressValidationMessages {
  addressRequired?: string;
  addressMinLength?: string;
}

const DEFAULT_ADDRESS_MESSAGES: AddressValidationMessages = {
  addressRequired: "Adress är obligatorisk",
  addressMinLength: "Adress måste vara minst 5 tecken lång",
};

export const validateAddress = (address: string, messages?: AddressValidationMessages, lang?: Language): ValidationResult => {
  const v = commonValidationLabels(lang ?? getLanguage());
  const m = { ...{ addressRequired: v.addressRequired, addressMinLength: v.addressMinLength }, ...messages };
  const errors: string[] = [];

  if (!address.trim()) {
    errors.push(m.addressRequired!);
  } else {
    if (address.length < 5) {
      errors.push(m.addressMinLength!);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export interface PostalCodeValidationMessages {
  postalCodeRequired?: string;
  postalCodeFormat?: string;
}

const DEFAULT_POSTAL_CODE_MESSAGES: PostalCodeValidationMessages = {
  postalCodeRequired: "Postnummer är obligatoriskt",
  postalCodeFormat: "Postnummer måste vara 5 siffror",
};

export const validatePostalCode = (postalCode: string, messages?: PostalCodeValidationMessages, lang?: Language): ValidationResult => {
  const v = commonValidationLabels(lang ?? getLanguage());
  const m = { ...{ postalCodeRequired: v.postalCodeRequired, postalCodeFormat: v.postalCodeFormat }, ...messages };
  const errors: string[] = [];

  if (!postalCode.trim()) {
    errors.push(m.postalCodeRequired!);
  } else {
    const cleanPostal = postalCode.replace(/\s/g, '');
    if (!/^\d{5}$/.test(cleanPostal)) {
      errors.push(m.postalCodeFormat!);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export interface CityValidationMessages {
  cityRequired?: string;
  cityMinLength?: string;
  cityLettersOnly?: string;
}

const DEFAULT_CITY_MESSAGES: CityValidationMessages = {
  cityRequired: "Ort är obligatorisk",
  cityMinLength: "Ort måste vara minst 2 tecken lång",
  cityLettersOnly: "Ort får endast innehålla bokstäver och mellanslag",
};

export const validateCity = (city: string, messages?: CityValidationMessages, lang?: Language): ValidationResult => {
  const v = commonValidationLabels(lang ?? getLanguage());
  const m = { ...{ cityRequired: v.cityRequired, cityMinLength: v.cityMinLength, cityLettersOnly: v.cityLettersOnly }, ...messages };
  const errors: string[] = [];

  if (!city.trim()) {
    errors.push(m.cityRequired!);
  } else {
    if (city.length < 2) {
      errors.push(m.cityMinLength!);
    }
    if (!/^[A-ZÅÄÖa-zåäö\s]+$/.test(city)) {
      errors.push(m.cityLettersOnly!);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/** Optional email: valid if empty, otherwise same as validateEmail format. */
export const validateEmailOptional = (email: string, messages?: EmailValidationMessages, lang?: Language): ValidationResult => {
  if (!email.trim()) return { isValid: true, errors: [] };
  const v = commonValidationLabels(lang ?? getLanguage());
  const m = { ...{ invalidEmail: v.invalidEmail }, ...messages };
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.trim())) {
    return { isValid: false, errors: [m.invalidEmail!] };
  }
  return { isValid: true, errors: [] };
};

/** Optional phone: digits only, min 7 digits when provided. */
export const validatePhoneDigitsOnlyMin7 = (phone: string, messages?: PhoneValidationMessages, lang?: Language): ValidationResult => {
  const v = commonValidationLabels(lang ?? getLanguage());
  const m = { ...{ phoneFormat: (v as Record<string, string>).phoneDigitsOnly ?? v.phoneFormat, phoneMinLength: v.phoneMinLength }, ...messages };
  const trimmed = phone.trim();
  if (!trimmed) return { isValid: true, errors: [] };
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length !== trimmed.length) {
    return { isValid: false, errors: [m.phoneFormat!] };
  }
  if (digits.length < 7) {
    return { isValid: false, errors: [m.phoneMinLength!] };
  }
  return { isValid: true, errors: [] };
};

/** Optional postal code: digits only when provided. */
export const validatePostalCodeDigitsOnly = (postalCode: string, messages?: PostalCodeValidationMessages, lang?: Language): ValidationResult => {
  const v = commonValidationLabels(lang ?? getLanguage());
  const digitsOnlyMsg = (v as Record<string, string>).postalCodeDigitsOnly ?? v.postalCodeFormat;
  const m = { ...{ postalCodeFormat: digitsOnlyMsg }, ...messages };
  const trimmed = postalCode.trim();
  if (!trimmed) return { isValid: true, errors: [] };
  if (!/^\d+$/.test(trimmed)) {
    return { isValid: false, errors: [m.postalCodeFormat!] };
  }
  return { isValid: true, errors: [] };
};

/** Optional city: letters and spaces only when provided. */
export const validateCityOptional = (city: string, messages?: CityValidationMessages, lang?: Language): ValidationResult => {
  const v = commonValidationLabels(lang ?? getLanguage());
  const m = { ...{ cityLettersOnly: v.cityLettersOnly }, ...messages };
  const trimmed = city.trim();
  if (!trimmed) return { isValid: true, errors: [] };
  if (!/^[A-ZÅÄÖa-zåäö\s]+$/.test(trimmed)) {
    return { isValid: false, errors: [m.cityLettersOnly!] };
  }
  return { isValid: true, errors: [] };
};

export interface CountryValidationMessages {
  countryLettersOnly?: string;
}

/** Optional country: letters and spaces only when provided. */
export const validateCountryOptional = (country: string, messages?: CountryValidationMessages, lang?: Language): ValidationResult => {
  const v = commonValidationLabels(lang ?? getLanguage());
  const countryLettersOnly = (v as Record<string, string>).countryLettersOnly ?? (v as Record<string, string>).cityLettersOnly;
  const m = { ...{ countryLettersOnly }, ...messages };
  const trimmed = country.trim();
  if (!trimmed) return { isValid: true, errors: [] };
  if (!/^[A-ZÅÄÖa-zåäö\s]+$/.test(trimmed)) {
    return { isValid: false, errors: [m.countryLettersOnly!] };
  }
  return { isValid: true, errors: [] };
};

/** Dealer portal name: required, letters and spaces only, no digits. No "two words" requirement. */
export const validateNameDealerPortal = (name: string, messages?: NameValidationMessages, lang?: Language): ValidationResult => {
  const v = commonValidationLabels(lang ?? getLanguage());
  const m = { ...{ nameRequired: v.nameRequired, nameNoDigits: v.nameNoDigits, nameLettersOnly: v.nameLettersOnly, nameMinLength: v.nameMinLength }, ...messages };
  const errors: string[] = [];
  if (!name.trim()) {
    errors.push(m.nameRequired!);
  } else {
    if (/\d/.test(name)) errors.push(m.nameNoDigits!);
    if (!/^[A-ZÅÄÖa-zåäö\s]+$/.test(name)) errors.push(m.nameLettersOnly!);
    if (name.trim().length < 2) errors.push(m.nameMinLength!);
  }
  return { isValid: errors.length === 0, errors };
};

export interface PasswordValidationMessages {
  minLengthError?: string;
  hasLowercaseError?: string;
  hasUppercaseError?: string;
  hasNumberError?: string;
  hasSpecialCharError?: string;
}

const DEFAULT_PASSWORD_MESSAGES: PasswordValidationMessages = {
  minLengthError: "Lösenordet måste vara minst 8 tecken långt",
  hasLowercaseError: "Lösenordet måste innehålla minst en liten bokstav",
  hasUppercaseError: "Lösenordet måste innehålla minst en stor bokstav",
  hasNumberError: "Lösenordet måste innehålla minst en siffra",
  hasSpecialCharError: "Lösenordet måste innehålla minst ett specialtecken",
};

export const validatePassword = (
  password: string,
  messages?: PasswordValidationMessages,
  lang?: Language
): PasswordValidationResult => {
  const v = commonValidationLabels(lang ?? getLanguage());
  const m = { ...{ minLengthError: v.passwordMinLength, hasLowercaseError: v.passwordLowercase, hasUppercaseError: v.passwordUppercase, hasNumberError: v.passwordNumber, hasSpecialCharError: v.passwordSpecialChar }, ...messages };
  const errors: string[] = [];
  const requirements = {
    minLength: password.length >= 8,
    hasLowercase: /[a-z]/.test(password),
    hasUppercase: /[A-Z]/.test(password),
    hasNumber: /\d/.test(password),
    // eslint-disable-next-line no-useless-escape -- [ and / are literal in character class
    hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
  };

  if (!requirements.minLength) errors.push(m.minLengthError!);
  if (!requirements.hasLowercase) errors.push(m.hasLowercaseError!);
  if (!requirements.hasUppercase) errors.push(m.hasUppercaseError!);
  if (!requirements.hasNumber) errors.push(m.hasNumberError!);
  if (!requirements.hasSpecialChar) errors.push(m.hasSpecialCharError!);

  return {
    isValid: errors.length === 0,
    errors,
    requirements
  };
};

export const getPasswordStrength = (password: string): 'weak' | 'medium' | 'strong' => {
  const validation = validatePassword(password);
  const metRequirements = Object.values(validation.requirements).filter(Boolean).length;

  if (metRequirements < 3) return 'weak';
  if (metRequirements < 5) return 'medium';
  return 'strong';
};

export interface PasswordConfirmationMessages {
  confirmRequired?: string;
  passwordsDoNotMatch?: string;
}

const DEFAULT_PASSWORD_CONFIRM_MESSAGES: PasswordConfirmationMessages = {
  confirmRequired: "Bekräfta lösenord är obligatoriskt",
  passwordsDoNotMatch: "Lösenorden matchar inte",
};

export const validatePasswordConfirmation = (
  password: string,
  confirmPassword: string,
  messages?: PasswordConfirmationMessages,
  lang?: Language
): ValidationResult => {
  const v = commonValidationLabels(lang ?? getLanguage());
  const m = { ...{ confirmRequired: v.confirmPasswordRequired, passwordsDoNotMatch: v.passwordsDoNotMatch }, ...messages };
  const errors: string[] = [];

  if (!confirmPassword.trim()) {
    errors.push(m.confirmRequired!);
  } else if (password !== confirmPassword) {
    errors.push(m.passwordsDoNotMatch!);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export interface ContactPersonValidationMessages {
  contactPersonRequired?: string;
  contactPersonMinLength?: string;
  contactPersonFormat?: string;
}

const DEFAULT_CONTACT_PERSON_MESSAGES: ContactPersonValidationMessages = {
  contactPersonRequired: "Kontaktperson är obligatorisk",
  contactPersonMinLength: "Kontaktperson måste vara minst 2 tecken lång",
  contactPersonFormat: "Kontaktperson får endast innehålla bokstäver, mellanslag, bindestreck och punkter",
};

export const validateContactPerson = (contactPerson: string, messages?: ContactPersonValidationMessages, lang?: Language): ValidationResult => {
  const v = commonValidationLabels(lang ?? getLanguage());
  const m = { ...{ contactPersonRequired: v.contactPersonRequired, contactPersonMinLength: v.contactPersonMinLength, contactPersonFormat: v.contactPersonFormat }, ...messages };
  const errors: string[] = [];

  if (!contactPerson.trim()) {
    errors.push(m.contactPersonRequired!);
  } else {
    if (contactPerson.length < 2) {
      errors.push(m.contactPersonMinLength!);
    }
    if (!/^[A-ZÅÄÖa-zåäö\s\-.]+$/.test(contactPerson)) {
      errors.push(m.contactPersonFormat!);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}; 